import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  FileText, 
  Calendar, 
  Download, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  BarChart3,
  Eye,
  FileSpreadsheet
} from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('es-MX');
};

export default function Reportes({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("contrato"); // "contrato", "propietario", "estado_cuenta", or "cobranza"
  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  
  // Date filter state
  const [filterType, setFilterType] = useState("mes_actual");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detalleData, setDetalleData] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  
  // Estado de Cuenta state
  const [propietarios, setPropietarios] = useState([]);
  const [selectedPropietario, setSelectedPropietario] = useState("");
  const [estadoCuentaData, setEstadoCuentaData] = useState(null);
  const [estadoCuentaLoading, setEstadoCuentaLoading] = useState(false);
  
  // Cobranza report state
  const [cobranzaData, setCobranzaData] = useState(null);
  const [cobranzaLoading, setCobranzaLoading] = useState(false);
  const [cobranzaStatusFilter, setCobranzaStatusFilter] = useState("todos");
  
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === "estado_cuenta") {
      fetchPropietarios();
    } else if (activeTab === "cobranza") {
      fetchCobranzaReport();
    } else {
      fetchReporte();
    }
  }, [activeTab, filterType, dateRange]);
  
  useEffect(() => {
    if (activeTab === "estado_cuenta" && selectedPropietario) {
      fetchEstadoCuenta();
    }
  }, [selectedPropietario, filterType, dateRange]);

  useEffect(() => {
    if (activeTab === "cobranza") {
      fetchCobranzaReport();
    }
  }, [cobranzaStatusFilter]);

  const fetchCobranzaReport = async () => {
    setCobranzaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const { startDate, endDate } = getDateRange();
      
      const response = await axios.get(
        `${API}/reportes/cobranza?start_date=${startDate}&end_date=${endDate}&estado=${cobranzaStatusFilter}`,
        { headers }
      );
      
      setCobranzaData(response.data);
    } catch (error) {
      toast.error("Error al cargar reporte de cobranza");
      console.error(error);
    } finally {
      setCobranzaLoading(false);
    }
  };

  const fetchPropietarios = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/reportes/propietarios-lista`, { headers });
      setPropietarios(response.data);
      // Auto-select first propietario if list is not empty
      if (response.data.length > 0 && !selectedPropietario) {
        setSelectedPropietario(response.data[0].email);
      }
    } catch (error) {
      toast.error("Error al cargar propietarios");
      console.error(error);
    }
  };

  const fetchEstadoCuenta = async () => {
    if (!selectedPropietario) return;
    
    setEstadoCuentaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const { startDate, endDate } = getDateRange();
      
      const response = await axios.get(
        `${API}/reportes/estado-cuenta/${encodeURIComponent(selectedPropietario)}?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      
      setEstadoCuentaData(response.data);
    } catch (error) {
      toast.error("Error al cargar estado de cuenta");
      console.error(error);
    } finally {
      setEstadoCuentaLoading(false);
    }
  };

  const getDateRange = () => {
    let startDate = "";
    let endDate = "";
    
    if (filterType === "mes_actual") {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startDate = firstDay.toISOString().split('T')[0];
      endDate = lastDay.toISOString().split('T')[0];
    } else if (filterType === "ultimos_30") {
      const today = new Date();
      const past = new Date(today);
      past.setDate(past.getDate() - 30);
      startDate = past.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (filterType === "ultimos_60") {
      const today = new Date();
      const past = new Date(today);
      past.setDate(past.getDate() - 60);
      startDate = past.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (filterType === "personalizado" && dateRange.from && dateRange.to) {
      startDate = dateRange.from.toISOString().split('T')[0];
      endDate = dateRange.to.toISOString().split('T')[0];
    }
    
    return { startDate, endDate };
  };

  const fetchReporte = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const { startDate, endDate } = getDateRange();

      const endpoint = activeTab === "contrato" 
        ? `${API}/reportes/por-contrato` 
        : `${API}/reportes/por-propietario`;
      
      const response = await axios.get(
        `${endpoint}?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      
      setReporteData(response.data);
    } catch (error) {
      toast.error("Error al cargar el reporte");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (item) => {
    setDetalleLoading(true);
    setShowDetailModal(true);
    
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const { startDate, endDate } = getDateRange();
      
      const endpoint = activeTab === "contrato"
        ? `${API}/reportes/detalle-contrato/${item.contrato_id}`
        : `${API}/reportes/detalle-propietario/${item.propietario_email}`;
      
      const response = await axios.get(
        `${endpoint}?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      
      setDetalleData(response.data);
    } catch (error) {
      toast.error("Error al cargar el detalle");
      console.error(error);
      setShowDetailModal(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleDownloadDetalle = () => {
    if (!detalleData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPos = 20;

    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Estado de Cuenta Detallado', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Period
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Período: ${getPeriodoLabel()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Contract/Owner Info
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    if (activeTab === "contrato") {
      doc.text(`Contrato: ${detalleData.contrato.numero_contrato}`, 14, yPos);
      yPos += 7;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text(`Inmueble: ${detalleData.contrato.inmueble_nombre}`, 14, yPos);
      yPos += 5;
      doc.text(`Propietario: ${detalleData.contrato.propietario_nombre}`, 14, yPos);
      yPos += 5;
      doc.text(`Renta Mensual: ${formatCurrency(detalleData.contrato.renta_mensual)}`, 14, yPos);
      yPos += 10;
    } else {
      doc.text(`Propietario: ${detalleData.propietario.nombre}`, 14, yPos);
      yPos += 7;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text(`Contratos: ${detalleData.propietario.contratos_count}`, 14, yPos);
      yPos += 10;
    }

    // Summary boxes
    doc.setFillColor(240, 240, 240);
    doc.rect(14, yPos, 60, 20, 'F');
    doc.rect(77, yPos, 60, 20, 'F');
    doc.rect(140, yPos, 57, 20, 'F');

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('TOTAL INGRESOS', 16, yPos + 5);
    doc.text('TOTAL GASTOS', 79, yPos + 5);
    doc.text('BALANCE', 142, yPos + 5);

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(34, 197, 94); // Green
    doc.text(formatCurrency(detalleData.totales.ingresos), 16, yPos + 14);
    doc.setTextColor(239, 68, 68); // Red
    doc.text(formatCurrency(detalleData.totales.gastos), 79, yPos + 14);
    // Blue or Orange
    if (detalleData.totales.balance >= 0) {
      doc.setTextColor(37, 99, 235);
    } else {
      doc.setTextColor(249, 115, 22);
    }
    doc.text(formatCurrency(detalleData.totales.balance), 142, yPos + 14);
    
    doc.setTextColor(0, 0, 0);
    yPos += 30;

    // Ingresos Table
    if (detalleData.ingresos.length > 0) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Ingresos', 14, yPos);
      yPos += 5;

      const ingresosColumns = activeTab === "contrato" 
        ? ['Fecha', 'Concepto', 'Método', 'Importe']
        : ['Fecha', 'Concepto', 'Contrato', 'Inmueble', 'Método', 'Importe'];

      const ingresosRows = detalleData.ingresos.map(ing => {
        const baseRow = [
          formatDate(ing.fecha_pago),
          ing.cargo_descripcion || '-',
        ];
        
        if (activeTab === "propietario") {
          baseRow.push(
            ing.contrato_numero || '-',
            ing.inmueble_nombre || '-'
          );
        }
        
        baseRow.push(
          ing.metodo_pago || '-',
          formatCurrency(ing.importe)
        );
        
        return baseRow;
      });

      autoTable(doc, {
        startY: yPos,
        head: [ingresosColumns],
        body: ingresosRows,
        theme: 'grid',
        headStyles: { fillColor: [34, 197, 94], fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: activeTab === "contrato" ? {
          3: { halign: 'right', fontStyle: 'bold' }
        } : {
          5: { halign: 'right', fontStyle: 'bold' }
        }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Check if we need a new page
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Gastos Table
    if (detalleData.gastos.length > 0) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Gastos', 14, yPos);
      yPos += 5;

      const gastosColumns = activeTab === "contrato"
        ? ['Fecha', 'Concepto', 'Categoría', 'Monto']
        : ['Fecha', 'Concepto', 'Contrato', 'Inmueble', 'Categoría', 'Monto'];

      const gastosRows = detalleData.gastos.map(gasto => {
        const baseRow = [
          formatDate(gasto.fecha_gasto),
          gasto.concepto,
        ];
        
        if (activeTab === "propietario") {
          baseRow.push(
            gasto.contrato_numero || '-',
            gasto.inmueble_nombre || '-'
          );
        }
        
        baseRow.push(
          gasto.categoria_nombre || '-',
          formatCurrency(gasto.monto)
        );
        
        return baseRow;
      });

      autoTable(doc, {
        startY: yPos,
        head: [gastosColumns],
        body: gastosRows,
        theme: 'grid',
        headStyles: { fillColor: [239, 68, 68], fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: activeTab === "contrato" ? {
          3: { halign: 'right', fontStyle: 'bold' }
        } : {
          5: { halign: 'right', fontStyle: 'bold' }
        }
      });
    }

    // Footer
    const timestamp = new Date().toLocaleString('es-MX');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generado el ${timestamp}`, 14, doc.internal.pageSize.height - 10);

    // Save
    const fileName = activeTab === "contrato"
      ? `Estado_Cuenta_${detalleData.contrato.numero_contrato}_${new Date().toISOString().split('T')[0]}.pdf`
      : `Estado_Cuenta_${detalleData.propietario.nombre.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(fileName);
    toast.success("PDF descargado exitosamente");
  };

  const handleDownload = () => {
    if (!reporteData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPos = 20;

    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Reporte Financiero', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Period and Type
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${activeTab === "contrato" ? "Por Contrato" : "Por Propietario"} - ${getPeriodoLabel()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Summary boxes
    doc.setFillColor(240, 240, 240);
    doc.rect(14, yPos, 60, 20, 'F');
    doc.rect(77, yPos, 60, 20, 'F');
    doc.rect(140, yPos, 57, 20, 'F');

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('TOTAL INGRESOS', 16, yPos + 5);
    doc.text('TOTAL GASTOS', 79, yPos + 5);
    doc.text('BALANCE', 142, yPos + 5);

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(34, 197, 94); // Green
    doc.text(formatCurrency(reporteData.totales.ingresos), 16, yPos + 14);
    doc.setTextColor(239, 68, 68); // Red
    doc.text(formatCurrency(reporteData.totales.gastos), 79, yPos + 14);
    // Blue or Orange
    if (reporteData.totales.balance >= 0) {
      doc.setTextColor(37, 99, 235);
    } else {
      doc.setTextColor(249, 115, 22);
    }
    doc.text(formatCurrency(reporteData.totales.balance), 142, yPos + 14);
    
    doc.setTextColor(0, 0, 0);
    yPos += 30;

    // Table
    let columns, rows;
    
    if (activeTab === "contrato") {
      columns = ['Contrato', 'Inmueble', 'Propietario', 'Ingresos', 'Gastos', 'Balance'];
      rows = reporteData.items.map(item => [
        item.numero_contrato,
        item.inmueble_nombre,
        item.propietario_nombre,
        formatCurrency(item.ingresos),
        formatCurrency(item.gastos),
        formatCurrency(item.balance)
      ]);
    } else {
      columns = ['Propietario', 'Contratos', 'Ingresos', 'Gastos', 'Balance'];
      rows = reporteData.items.map(item => [
        item.propietario_nombre,
        item.contratos_count.toString(),
        formatCurrency(item.ingresos),
        formatCurrency(item.gastos),
        formatCurrency(item.balance)
      ]);
    }

    // Add totals row
    rows.push([
      activeTab === "contrato" ? 'TOTALES' : 'TOTALES',
      activeTab === "contrato" ? '' : '',
      activeTab === "contrato" ? '' : '',
      formatCurrency(reporteData.totales.ingresos),
      formatCurrency(reporteData.totales.gastos),
      formatCurrency(reporteData.totales.balance)
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [columns],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [79, 70, 229], fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: activeTab === "contrato" ? {
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right', fontStyle: 'bold' }
      } : {
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right', fontStyle: 'bold' }
      },
      didParseCell: function(data) {
        // Make totals row bold
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240];
        }
      }
    });

    // Footer
    const timestamp = new Date().toLocaleString('es-MX');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generado el ${timestamp}`, 14, doc.internal.pageSize.height - 10);

    // Save
    const fileName = `Reporte_Financiero_${activeTab === "contrato" ? "Contratos" : "Propietarios"}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success("PDF descargado exitosamente");
  };

  const handleDownloadEstadoCuenta = async () => {
    if (!estadoCuentaData) return;

    const doc = new jsPDF({ orientation: 'portrait' });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPos = 15;

    // Try to load logo image
    let logoLoaded = false;
    try {
      const response = await fetch('/assets/logo-rotco.jpg');
      const blob = await response.blob();
      const reader = new FileReader();
      const logoDataUrl = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      doc.addImage(logoDataUrl, 'JPEG', 14, 8, 45, 28);
      logoLoaded = true;
    } catch (e) {
      console.log('Logo load failed, using text fallback');
    }
    
    // Fallback: Draw ROTCO text logo if image failed
    if (!logoLoaded) {
      doc.setFontSize(28);
      doc.setTextColor(147, 112, 219); // Purple/Violet color
      doc.setFont(undefined, 'bold');
      doc.text('ROTCO', 14, 28);
      
      // Draw blue oval with "4" symbol
      doc.setFillColor(100, 149, 237);
      doc.ellipse(34, 42, 12, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text('4', 31, 45);
    }

    // Title - Centered
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text('Estado de Cuenta Detallado', pageWidth / 2, 25, { align: 'center' });

    // Period - Right side
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Período: ${getPeriodoLabel()}`, pageWidth - 14, 25, { align: 'right' });

    yPos = 45;

    // Contract info box - Left side
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Propietario:', 14, yPos);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(estadoCuentaData.propietario.nombre, 45, yPos);
    
    yPos += 6;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Contratos Activos:', 14, yPos);
    doc.setTextColor(0, 0, 0);
    // Count only active contracts (non-archived)
    const contratosActivos = estadoCuentaData.rows.filter(r => r.estatus !== 'Archivado').length;
    doc.text(String(contratosActivos), 50, yPos);

    yPos += 12;

    // Summary boxes
    const boxWidth = 55;
    const boxHeight = 18;
    const boxStartX = 14;
    const boxGap = 8;

    // Total Ingresos box
    doc.setFillColor(240, 240, 240);
    doc.rect(boxStartX, yPos, boxWidth, boxHeight, 'F');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('TOTAL INGRESOS', boxStartX + 3, yPos + 5);
    doc.setFontSize(14);
    doc.setTextColor(34, 197, 94); // Green
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(estadoCuentaData.totales.total_ingresos), boxStartX + 3, yPos + 13);

    // Total Gastos box
    const box2X = boxStartX + boxWidth + boxGap;
    doc.setFillColor(240, 240, 240);
    doc.rect(box2X, yPos, boxWidth, boxHeight, 'F');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text('TOTAL GASTOS', box2X + 3, yPos + 5);
    doc.setFontSize(14);
    doc.setTextColor(239, 68, 68); // Red
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(estadoCuentaData.totales.total_egresos), box2X + 3, yPos + 13);

    // Balance box
    const box3X = box2X + boxWidth + boxGap;
    doc.setFillColor(240, 240, 240);
    doc.rect(box3X, yPos, boxWidth, boxHeight, 'F');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text('BALANCE', box3X + 3, yPos + 5);
    doc.setFontSize(14);
    const balanceColor = estadoCuentaData.totales.balance >= 0 ? [37, 99, 235] : [249, 115, 22];
    doc.setTextColor(...balanceColor);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(estadoCuentaData.totales.balance), box3X + 3, yPos + 13);

    yPos += boxHeight + 12;

    // Build columns dynamically
    const ingresoColumns = ['Renta', 'Depósito'];
    const gastoColumns = estadoCuentaData.categorias_gasto || [];
    
    const columns = [
      'Inmueble',
      'Contrato',
      ...ingresoColumns,
      'Total Ing.',
      ...gastoColumns.map(c => c.length > 8 ? c.substring(0, 8) + '.' : c),
      'Total Egr.',
      'Balance'
    ];

    // Build rows
    const rows = estadoCuentaData.rows.map(row => {
      const rowData = [
        row.inmueble.length > 20 ? row.inmueble.substring(0, 20) + '...' : row.inmueble,
        row.numero_contrato || '-',
        formatCurrency(row.renta),
        formatCurrency(row.deposito),
        formatCurrency(row.total_ingresos),
      ];
      
      gastoColumns.forEach(cat => {
        rowData.push(formatCurrency(row.gastos_por_categoria[cat] || 0));
      });
      
      rowData.push(formatCurrency(row.total_egresos));
      rowData.push(formatCurrency(row.balance));
      
      return rowData;
    });

    // Add totals row
    const totalsRow = [
      'TOTALES',
      '',
      formatCurrency(estadoCuentaData.totales.renta),
      formatCurrency(estadoCuentaData.totales.deposito),
      formatCurrency(estadoCuentaData.totales.total_ingresos),
    ];
    
    gastoColumns.forEach(cat => {
      totalsRow.push(formatCurrency(estadoCuentaData.totales.gastos_por_categoria[cat] || 0));
    });
    
    totalsRow.push(formatCurrency(estadoCuentaData.totales.total_egresos));
    totalsRow.push(formatCurrency(estadoCuentaData.totales.balance));
    
    rows.push(totalsRow);

    // Calculate column styles dynamically
    const columnStyles = {};
    columnStyles[0] = { cellWidth: 35 };
    columnStyles[1] = { cellWidth: 20 };
    for (let i = 2; i < columns.length; i++) {
      columnStyles[i] = { halign: 'right', cellWidth: 'auto' };
    }
    
    const totalEgresosIdx = 4 + gastoColumns.length + 1;
    const balanceIdx = totalEgresosIdx + 1;

    autoTable(doc, {
      startY: yPos,
      head: [columns],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [79, 70, 229], fontSize: 6, cellPadding: 1.5 },
      styles: { fontSize: 6, cellPadding: 1.5 },
      columnStyles: columnStyles,
      didParseCell: function(data) {
        // Make totals row bold with gray background
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [240, 240, 240];
        }
        // Color for balance column
        if (data.column.index === balanceIdx && data.section === 'body') {
          const value = parseFloat(data.cell.raw.replace(/[^0-9.-]/g, '')) || 0;
          if (value >= 0) {
            data.cell.styles.textColor = [34, 197, 94];
          } else {
            data.cell.styles.textColor = [239, 68, 68];
          }
        }
        // Color headers for income (green) and expenses (red)
        if (data.section === 'head') {
          const colIdx = data.column.index;
          if (colIdx >= 2 && colIdx <= 4) {
            data.cell.styles.fillColor = [34, 197, 94]; // Green for income
          } else if (colIdx > 4 && colIdx < balanceIdx) {
            data.cell.styles.fillColor = [239, 68, 68]; // Red for expenses
          } else if (colIdx === balanceIdx) {
            data.cell.styles.fillColor = [37, 99, 235]; // Blue for balance
          }
        }
      }
    });

    // Footer
    const footerY = pageHeight - 15;
    
    // Line separator
    doc.setDrawColor(200, 200, 200);
    doc.line(14, footerY - 5, pageWidth - 14, footerY - 5);
    
    // Timestamp - Left
    const timestamp = new Date().toLocaleString('es-MX');
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.setFont(undefined, 'normal');
    doc.text(`Generado el ${timestamp}`, 14, footerY);
    
    // Company info - Center
    doc.setFont(undefined, 'bold');
    doc.text('Grupo Inmobiliario Alarmen', pageWidth / 2, footerY - 2, { align: 'center' });
    doc.setFont(undefined, 'normal');
    doc.text('Acueducto 2380 D1 | Plaza Pabellón | www.alarmen.com.mx | 333 817 3010', pageWidth / 2, footerY + 2, { align: 'center' });

    // Save
    const fileName = `Estado_Cuenta_${estadoCuentaData.propietario.nombre.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success("PDF descargado exitosamente");
  };

  const handleDownloadEstadoCuentaExcel = () => {
    if (!estadoCuentaData) return;

    // Build header row
    const gastoColumns = estadoCuentaData.categorias_gasto || [];
    const headers = [
      'Inmueble',
      'Contrato',
      'Renta',
      'Depósito',
      'Total Ingresos',
      ...gastoColumns,
      'Total Egresos',
      'Balance'
    ];

    // Build data rows
    const dataRows = estadoCuentaData.rows.map(row => {
      const rowData = [
        row.inmueble,
        row.numero_contrato || '-',
        row.renta,
        row.deposito,
        row.total_ingresos,
      ];
      
      gastoColumns.forEach(cat => {
        rowData.push(row.gastos_por_categoria[cat] || 0);
      });
      
      rowData.push(row.total_egresos);
      rowData.push(row.balance);
      
      return rowData;
    });

    // Add totals row
    const totalsRow = [
      'TOTALES',
      '',
      estadoCuentaData.totales.renta,
      estadoCuentaData.totales.deposito,
      estadoCuentaData.totales.total_ingresos,
    ];
    
    gastoColumns.forEach(cat => {
      totalsRow.push(estadoCuentaData.totales.gastos_por_categoria[cat] || 0);
    });
    
    totalsRow.push(estadoCuentaData.totales.total_egresos);
    totalsRow.push(estadoCuentaData.totales.balance);
    
    dataRows.push(totalsRow);

    // Create worksheet
    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    const colWidths = headers.map((h, i) => {
      if (i === 0) return { wch: 35 }; // Inmueble
      if (i === 1) return { wch: 15 }; // Contrato
      return { wch: 15 }; // Numbers
    });
    ws['!cols'] = colWidths;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estado de Cuenta');

    // Add info sheet
    const contratosActivosExcel = estadoCuentaData.rows.filter(r => r.estatus !== 'Archivado').length;
    const infoData = [
      ['Estado de Cuenta por Propietario'],
      [''],
      ['Propietario:', estadoCuentaData.propietario.nombre],
      ['Contratos Activos:', contratosActivosExcel],
      ['Período:', getPeriodoLabel()],
      ['Generado:', new Date().toLocaleString('es-MX')],
    ];
    const wsInfo = XLSX.utils.aoa_to_sheet(infoData);
    wsInfo['!cols'] = [{ wch: 15 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsInfo, 'Información');

    // Save file
    const fileName = `Estado_Cuenta_${estadoCuentaData.propietario.nombre.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Excel descargado exitosamente");
  };

  // Download Cobranza as PDF
  const handleDownloadCobranzaPDF = () => {
    if (!cobranzaData || cobranzaData.cargos.length === 0) return;
    
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Reporte de Cobranza', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Período: ${getPeriodoLabel()}`, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Generado: ${new Date().toLocaleString('es-MX')}`, pageWidth / 2, 35, { align: 'center' });
    
    // Summary stats
    doc.setFontSize(10);
    doc.text(`Total Cargos: ${cobranzaData.totales.total_cargos}`, 14, 45);
    doc.text(`Importe Total: ${formatCurrency(cobranzaData.totales.importe_total)}`, 80, 45);
    doc.text(`Total Pagado: ${formatCurrency(cobranzaData.totales.pagado)}`, 160, 45);
    doc.text(`Saldo Pendiente: ${formatCurrency(cobranzaData.totales.saldo_pendiente)}`, 230, 45);
    
    // Table
    const tableData = cobranzaData.cargos.map(cargo => [
      cargo.inmueble_nombre || '-',
      cargo.propietario_nombre || '-',
      cargo.tipo || '-',
      cargo.descripcion,
      formatDate(cargo.fecha_vencimiento),
      formatCurrency(cargo.importe),
      formatCurrency(cargo.importe - cargo.saldo_pendiente),
      formatCurrency(cargo.saldo_pendiente),
      cargo.estado
    ]);
    
    // Add totals row
    tableData.push([
      'TOTALES',
      '',
      '',
      `${cobranzaData.totales.total_cargos} cargos`,
      '',
      formatCurrency(cobranzaData.totales.importe_total),
      formatCurrency(cobranzaData.totales.pagado),
      formatCurrency(cobranzaData.totales.saldo_pendiente),
      ''
    ]);
    
    autoTable(doc, {
      head: [['Inmueble', 'Propietario', 'Tipo', 'Descripción', 'Vencimiento', 'Importe', 'Pagado', 'Saldo', 'Estado']],
      body: tableData,
      startY: 52,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20 },
        3: { cellWidth: 40 },
        4: { cellWidth: 22 },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 25, halign: 'right' },
        7: { cellWidth: 25, halign: 'right' },
        8: { cellWidth: 20, halign: 'center' }
      },
      didParseCell: function(data) {
        // Style the last row as totals
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    
    const fileName = `Reporte_Cobranza_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success("PDF descargado exitosamente");
  };

  // Download Cobranza as Excel
  const handleDownloadCobranzaExcel = () => {
    if (!cobranzaData || cobranzaData.cargos.length === 0) return;
    
    const wb = XLSX.utils.book_new();
    
    // Main data sheet
    const wsData = cobranzaData.cargos.map(cargo => ({
      'Inmueble': cargo.inmueble_nombre || '-',
      'Propietario': cargo.propietario_nombre || '-',
      'Tipo': cargo.tipo || '-',
      'Descripción': cargo.descripcion,
      'Fecha Vencimiento': cargo.fecha_vencimiento,
      'Importe': cargo.importe,
      'Pagado': cargo.importe - cargo.saldo_pendiente,
      'Saldo Pendiente': cargo.saldo_pendiente,
      'Estado': cargo.estado
    }));
    
    // Add totals row
    wsData.push({
      'Inmueble': 'TOTALES',
      'Propietario': '',
      'Tipo': '',
      'Descripción': `${cobranzaData.totales.total_cargos} cargos`,
      'Fecha Vencimiento': '',
      'Importe': cobranzaData.totales.importe_total,
      'Pagado': cobranzaData.totales.pagado,
      'Saldo Pendiente': cobranzaData.totales.saldo_pendiente,
      'Estado': ''
    });
    
    const ws = XLSX.utils.json_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 30 }, // Inmueble
      { wch: 25 }, // Propietario
      { wch: 15 }, // Tipo
      { wch: 35 }, // Descripción
      { wch: 15 }, // Fecha
      { wch: 15 }, // Importe
      { wch: 15 }, // Pagado
      { wch: 15 }, // Saldo
      { wch: 12 }  // Estado
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Cobranza');
    
    // Info sheet
    const infoData = [
      ['Reporte de Cobranza'],
      [''],
      ['Período:', getPeriodoLabel()],
      ['Total Cargos:', cobranzaData.totales.total_cargos],
      ['Importe Total:', cobranzaData.totales.importe_total],
      ['Total Pagado:', cobranzaData.totales.pagado],
      ['Saldo Pendiente:', cobranzaData.totales.saldo_pendiente],
      [''],
      ['Generado:', new Date().toLocaleString('es-MX')],
    ];
    const wsInfo = XLSX.utils.aoa_to_sheet(infoData);
    wsInfo['!cols'] = [{ wch: 20 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsInfo, 'Resumen');
    
    const fileName = `Reporte_Cobranza_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Excel descargado exitosamente");
  };

  const getPeriodoLabel = () => {
    if (filterType === "mes_actual") {
      const now = new Date();
      const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    }
    if (filterType === "ultimos_30") return "Últimos 30 Días";
    if (filterType === "ultimos_60") return "Últimos 60 Días";
    if (filterType === "personalizado" && dateRange.from && dateRange.to) {
      return `${dateRange.from.toLocaleDateString('es-MX')} - ${dateRange.to.toLocaleDateString('es-MX')}`;
    }
    return "Seleccionar período";
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reportes Financieros</h1>
            <p className="text-gray-500 mt-1">Visualiza ingresos y gastos por período</p>
          </div>
          {activeTab !== "estado_cuenta" ? (
            <Button onClick={handleDownload} variant="outline" data-testid="download-report-btn">
              <Download className="w-4 h-4 mr-2" />
              Descargar
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                onClick={handleDownloadEstadoCuentaExcel} 
                variant="outline" 
                disabled={!estadoCuentaData}
                data-testid="download-estado-cuenta-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button 
                onClick={handleDownloadEstadoCuenta} 
                variant="outline" 
                disabled={!estadoCuentaData}
                data-testid="download-estado-cuenta-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes_actual">Este mes</SelectItem>
                  <SelectItem value="ultimos_30">Últimos 30 días</SelectItem>
                  <SelectItem value="ultimos_60">Últimos 60 días</SelectItem>
                  <SelectItem value="personalizado">Personalizado</SelectItem>
                </SelectContent>
              </Select>

              {filterType === "personalizado" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline">
                      <Calendar className="w-4 h-4 mr-2" />
                      Seleccionar fechas
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab("contrato")}
            data-testid="tab-contrato"
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === "contrato"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Por Contrato
          </button>
          <button
            onClick={() => setActiveTab("propietario")}
            data-testid="tab-propietario"
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === "propietario"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Por Propietario
          </button>
          <button
            onClick={() => setActiveTab("estado_cuenta")}
            data-testid="tab-estado-cuenta"
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === "estado_cuenta"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Estado de Cuenta
          </button>
          <button
            onClick={() => setActiveTab("cobranza")}
            data-testid="tab-cobranza"
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === "cobranza"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Cobranza
          </button>
        </div>

        {/* Estado de Cuenta Tab Content */}
        {activeTab === "estado_cuenta" && (
          <>
            {/* Propietario Selector and Date Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  {/* First row: Propietario selector */}
                  <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar Propietario
                      </label>
                      <Select value={selectedPropietario} onValueChange={setSelectedPropietario}>
                        <SelectTrigger className="w-full" data-testid="select-propietario">
                          <SelectValue placeholder="Seleccionar propietario..." />
                        </SelectTrigger>
                        <SelectContent>
                          {propietarios.map((p) => (
                            <SelectItem key={p.email} value={p.email}>
                              {p.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Second row: Date filters */}
                  <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Período
                      </label>
                      <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-full md:w-[200px]" data-testid="select-periodo-estado">
                          <SelectValue placeholder="Período" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mes_actual">Este mes</SelectItem>
                          <SelectItem value="ultimos_30">Últimos 30 días</SelectItem>
                          <SelectItem value="ultimos_60">Últimos 60 días</SelectItem>
                          <SelectItem value="personalizado">Personalizado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {filterType === "personalizado" && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" data-testid="btn-select-dates-estado">
                            <Calendar className="w-4 h-4 mr-2" />
                            {dateRange.from && dateRange.to 
                              ? `${dateRange.from.toLocaleDateString('es-MX')} - ${dateRange.to.toLocaleDateString('es-MX')}`
                              : "Seleccionar fechas"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="range"
                            selected={dateRange}
                            onSelect={setDateRange}
                            numberOfMonths={2}
                          />
                        </PopoverContent>
                      </Popover>
                    )}

                    <Button 
                      onClick={fetchEstadoCuenta} 
                      disabled={!selectedPropietario || estadoCuentaLoading}
                      data-testid="btn-generar-estado"
                    >
                      {estadoCuentaLoading ? "Cargando..." : "Generar Reporte"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>


            {/* Estado de Cuenta Results */}
            {estadoCuentaLoading ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Generando estado de cuenta...</p>
                  </div>
                </CardContent>
              </Card>
            ) : estadoCuentaData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">Total Ingresos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-green-600">
                          {formatCurrency(estadoCuentaData.totales.total_ingresos)}
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">Total Egresos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-red-600">
                          {formatCurrency(estadoCuentaData.totales.total_egresos)}
                        </div>
                        <TrendingDown className="w-8 h-8 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">Balance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className={`text-2xl font-bold ${
                          estadoCuentaData.totales.balance >= 0 ? 'text-blue-600' : 'text-orange-600'
                        }`}>
                          {formatCurrency(estadoCuentaData.totales.balance)}
                        </div>
                        <DollarSign className="w-8 h-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Grid Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Estado de Cuenta - {estadoCuentaData.propietario.nombre} - {getPeriodoLabel()}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="estado-cuenta-table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                              Inmueble
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Contrato
                            </th>
                            <th colSpan={2} className="px-3 py-2 text-center text-xs font-medium text-green-600 uppercase tracking-wider bg-green-50">
                              Ingresos
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-green-700 uppercase tracking-wider bg-green-100">
                              Total Ing.
                            </th>
                            {estadoCuentaData.categorias_gasto.map((cat, idx) => (
                              <th key={idx} className="px-3 py-2 text-right text-xs font-medium text-red-600 uppercase tracking-wider bg-red-50">
                                {cat.length > 10 ? cat.substring(0, 10) + '.' : cat}
                              </th>
                            ))}
                            <th className="px-3 py-2 text-right text-xs font-medium text-red-700 uppercase tracking-wider bg-red-100">
                              Total Egr.
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-blue-700 uppercase tracking-wider bg-blue-100">
                              Balance
                            </th>
                          </tr>
                          <tr>
                            <th className="px-3 py-1 text-left text-xs text-gray-400 sticky left-0 bg-gray-50"></th>
                            <th className="px-3 py-1 text-left text-xs text-gray-400"></th>
                            <th className="px-3 py-1 text-right text-xs text-gray-500 bg-green-50">Renta</th>
                            <th className="px-3 py-1 text-right text-xs text-gray-500 bg-green-50">Depósito</th>
                            <th className="px-3 py-1 bg-green-100"></th>
                            {estadoCuentaData.categorias_gasto.map((_, idx) => (
                              <th key={idx} className="px-3 py-1 bg-red-50"></th>
                            ))}
                            <th className="px-3 py-1 bg-red-100"></th>
                            <th className="px-3 py-1 bg-blue-100"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {estadoCuentaData.rows.map((row, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                                {row.inmueble.length > 30 ? row.inmueble.substring(0, 30) + '...' : row.inmueble}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                {row.numero_contrato || '-'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-600 bg-green-50/30">
                                {formatCurrency(row.renta)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-600 bg-green-50/30">
                                {formatCurrency(row.deposito)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-semibold text-green-700 bg-green-100/50">
                                {formatCurrency(row.total_ingresos)}
                              </td>
                              {estadoCuentaData.categorias_gasto.map((cat, idx) => (
                                <td key={idx} className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-600 bg-red-50/30">
                                  {formatCurrency(row.gastos_por_categoria[cat] || 0)}
                                </td>
                              ))}
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-semibold text-red-700 bg-red-100/50">
                                {formatCurrency(row.total_egresos)}
                              </td>
                              <td className={`px-3 py-2 whitespace-nowrap text-sm text-right font-bold bg-blue-100/50 ${
                                row.balance >= 0 ? 'text-blue-600' : 'text-orange-600'
                              }`}>
                                {formatCurrency(row.balance)}
                              </td>
                            </tr>
                          ))}
                          {/* Totals Row */}
                          <tr className="bg-gray-100 font-bold">
                            <td className="px-3 py-2 text-sm text-gray-900 sticky left-0 bg-gray-100">
                              TOTALES
                            </td>
                            <td className="px-3 py-2"></td>
                            <td className="px-3 py-2 text-sm text-right text-green-600">
                              {formatCurrency(estadoCuentaData.totales.renta)}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-green-600">
                              {formatCurrency(estadoCuentaData.totales.deposito)}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-green-700 bg-green-200">
                              {formatCurrency(estadoCuentaData.totales.total_ingresos)}
                            </td>
                            {estadoCuentaData.categorias_gasto.map((cat, idx) => (
                              <td key={idx} className="px-3 py-2 text-sm text-right text-red-600">
                                {formatCurrency(estadoCuentaData.totales.gastos_por_categoria[cat] || 0)}
                              </td>
                            ))}
                            <td className="px-3 py-2 text-sm text-right text-red-700 bg-red-200">
                              {formatCurrency(estadoCuentaData.totales.total_egresos)}
                            </td>
                            <td className={`px-3 py-2 text-sm text-right bg-blue-200 ${
                              estadoCuentaData.totales.balance >= 0 ? 'text-blue-700' : 'text-orange-700'
                            }`}>
                              {formatCurrency(estadoCuentaData.totales.balance)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center text-gray-500">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p>Selecciona un propietario para generar el estado de cuenta</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Cobranza Tab Content */}
        {activeTab === "cobranza" && (
          <>
            {/* Filters Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Período
                    </label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-full md:w-[200px]" data-testid="select-periodo-cobranza">
                        <SelectValue placeholder="Período" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mes_actual">Este mes</SelectItem>
                        <SelectItem value="ultimos_30">Últimos 30 días</SelectItem>
                        <SelectItem value="ultimos_60">Últimos 60 días</SelectItem>
                        <SelectItem value="personalizado">Personalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {filterType === "personalizado" && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline">
                          <Calendar className="w-4 h-4 mr-2" />
                          {dateRange.from && dateRange.to 
                            ? `${dateRange.from.toLocaleDateString('es-MX')} - ${dateRange.to.toLocaleDateString('es-MX')}`
                            : "Seleccionar fechas"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="range"
                          selected={dateRange}
                          onSelect={setDateRange}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado
                    </label>
                    <Select value={cobranzaStatusFilter} onValueChange={setCobranzaStatusFilter}>
                      <SelectTrigger className="w-full md:w-[180px]" data-testid="select-estado-cobranza">
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos los estados</SelectItem>
                        <SelectItem value="Pendiente">Pendiente</SelectItem>
                        <SelectItem value="Parcial">Parcial</SelectItem>
                        <SelectItem value="Pagado">Pagado</SelectItem>
                        <SelectItem value="Vencido">Vencido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 ml-auto">
                    <Button 
                      onClick={handleDownloadCobranzaExcel} 
                      variant="outline" 
                      disabled={!cobranzaData || cobranzaData.cargos.length === 0}
                      data-testid="download-cobranza-excel-btn"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                    <Button 
                      onClick={handleDownloadCobranzaPDF} 
                      variant="outline" 
                      disabled={!cobranzaData || cobranzaData.cargos.length === 0}
                      data-testid="download-cobranza-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            {cobranzaData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Total Cargos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-gray-900">{cobranzaData.totales.total_cargos}</div>
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Importe Total</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-blue-600">{formatCurrency(cobranzaData.totales.importe_total)}</div>
                      <DollarSign className="w-8 h-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Total Pagado</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(cobranzaData.totales.pagado)}</div>
                      <TrendingUp className="w-8 h-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Saldo Pendiente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-orange-600">{formatCurrency(cobranzaData.totales.saldo_pendiente)}</div>
                      <TrendingDown className="w-8 h-8 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Cobranza Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Reporte de Cobranza - {getPeriodoLabel()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cobranzaLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : cobranzaData && cobranzaData.cargos.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Inmueble</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Propietario</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Descripción</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Vencimiento</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Importe</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Pagado</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Saldo</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cobranzaData.cargos.map((cargo, index) => (
                          <tr key={cargo.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="py-3 px-4 text-sm text-gray-900">{cargo.inmueble_nombre || '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{cargo.propietario_nombre || '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{cargo.tipo || '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-900">{cargo.descripcion}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">{formatDate(cargo.fecha_vencimiento)}</td>
                            <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">{formatCurrency(cargo.importe)}</td>
                            <td className="py-3 px-4 text-sm text-right text-green-600">{formatCurrency(cargo.importe - cargo.saldo_pendiente)}</td>
                            <td className="py-3 px-4 text-sm text-right text-orange-600">{formatCurrency(cargo.saldo_pendiente)}</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                cargo.estado === 'Pagado' ? 'bg-green-100 text-green-800' :
                                cargo.estado === 'Vencido' ? 'bg-red-100 text-red-800' :
                                cargo.estado === 'Parcial' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {cargo.estado}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100">
                        <tr className="font-bold">
                          <td colSpan="5" className="py-3 px-4 text-sm text-gray-900">TOTALES ({cobranzaData.totales.total_cargos} cargos)</td>
                          <td className="py-3 px-4 text-sm text-right text-gray-900">{formatCurrency(cobranzaData.totales.importe_total)}</td>
                          <td className="py-3 px-4 text-sm text-right text-green-600">{formatCurrency(cobranzaData.totales.pagado)}</td>
                          <td className="py-3 px-4 text-sm text-right text-orange-600">{formatCurrency(cobranzaData.totales.saldo_pendiente)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p>No se encontraron cargos en el período seleccionado</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Stats Cards - Only for contrato/propietario tabs */}
        {activeTab !== "estado_cuenta" && activeTab !== "cobranza" && reporteData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Ingresos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(reporteData.totales.ingresos)}
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Gastos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-red-600">
                    {formatCurrency(reporteData.totales.gastos)}
                  </div>
                  <TrendingDown className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Balance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className={`text-2xl font-bold ${
                    reporteData.totales.balance >= 0 ? 'text-blue-600' : 'text-orange-600'
                  }`}>
                    {formatCurrency(reporteData.totales.balance)}
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Report Table - Only for contrato/propietario tabs */}
        {activeTab !== "estado_cuenta" && activeTab !== "cobranza" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estado de Cuenta - {getPeriodoLabel()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Cargando reporte...</div>
              ) : !reporteData || reporteData.items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>No hay datos para mostrar en el período seleccionado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {activeTab === "contrato" ? (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Contrato
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Inmueble
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Propietario
                            </th>
                          </>
                        ) : (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Propietario
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Contratos
                            </th>
                          </>
                        )}
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ingresos
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gastos
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reporteData.items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          {activeTab === "contrato" ? (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {item.numero_contrato}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {item.inmueble_nombre}
                              </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.propietario_nombre}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.propietario_nombre}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {item.contratos_count}
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                          {formatCurrency(item.ingresos)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-red-600">
                          {formatCurrency(item.gastos)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${
                          item.balance >= 0 ? 'text-blue-600' : 'text-orange-600'
                        }`}>
                          {formatCurrency(item.balance)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetail(item)}
                            title="Ver detalle"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver detalle
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan={activeTab === "contrato" ? 3 : 2} className="px-6 py-4 text-sm text-gray-900">
                        TOTALES
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                        {formatCurrency(reporteData.totales.ingresos)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                        {formatCurrency(reporteData.totales.gastos)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                        reporteData.totales.balance >= 0 ? 'text-blue-600' : 'text-orange-600'
                      }`}>
                        {formatCurrency(reporteData.totales.balance)}
                      </td>
                      <td className="px-6 py-4"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detalle del {activeTab === "contrato" ? "Contrato" : "Propietario"} - {getPeriodoLabel()}
            </DialogTitle>
            <DialogDescription>
              Estado de cuenta detallado con lista de ingresos y gastos
            </DialogDescription>
          </DialogHeader>

          {detalleLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando detalle...</p>
            </div>
          ) : detalleData ? (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                {activeTab === "contrato" ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Contrato</p>
                      <p className="font-medium">{detalleData.contrato.numero_contrato}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Inmueble</p>
                      <p className="font-medium">{detalleData.contrato.inmueble_nombre}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Propietario</p>
                      <p className="font-medium">{detalleData.contrato.propietario_nombre}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Renta Mensual</p>
                      <p className="font-medium">{formatCurrency(detalleData.contrato.renta_mensual)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Propietario</p>
                      <p className="font-medium">{detalleData.propietario.nombre}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Contratos</p>
                      <p className="font-medium">{detalleData.propietario.contratos_count}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Total Ingresos</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(detalleData.totales.ingresos)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Total Gastos</p>
                    <p className="text-xl font-bold text-red-600">
                      {formatCurrency(detalleData.totales.gastos)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-500">Balance</p>
                    <p className={`text-xl font-bold ${
                      detalleData.totales.balance >= 0 ? 'text-blue-600' : 'text-orange-600'
                    }`}>
                      {formatCurrency(detalleData.totales.balance)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Ingresos Table */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Ingresos ({detalleData.ingresos.length})
                </h3>
                {detalleData.ingresos.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay ingresos registrados</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                          {activeTab === "propietario" && (
                            <>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contrato</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Inmueble</th>
                            </>
                          )}
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {detalleData.ingresos.map((ingreso, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm">{formatDate(ingreso.fecha_pago)}</td>
                            <td className="px-4 py-2 text-sm">{ingreso.cargo_descripcion || '-'}</td>
                            {activeTab === "propietario" && (
                              <>
                                <td className="px-4 py-2 text-sm">{ingreso.contrato_numero || '-'}</td>
                                <td className="px-4 py-2 text-sm">{ingreso.inmueble_nombre || '-'}</td>
                              </>
                            )}
                            <td className="px-4 py-2 text-sm">{ingreso.metodo_pago || '-'}</td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-green-600">
                              {formatCurrency(ingreso.importe)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Gastos Table */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  Gastos ({detalleData.gastos.length})
                </h3>
                {detalleData.gastos.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay gastos registrados</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                          {activeTab === "propietario" && (
                            <>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contrato</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Inmueble</th>
                            </>
                          )}
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {detalleData.gastos.map((gasto, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm">{formatDate(gasto.fecha_gasto)}</td>
                            <td className="px-4 py-2 text-sm">{gasto.concepto}</td>
                            {activeTab === "propietario" && (
                              <>
                                <td className="px-4 py-2 text-sm">{gasto.contrato_numero || '-'}</td>
                                <td className="px-4 py-2 text-sm">{gasto.inmueble_nombre || '-'}</td>
                              </>
                            )}
                            <td className="px-4 py-2 text-sm">{gasto.categoria_nombre || '-'}</td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-red-600">
                              {formatCurrency(gasto.monto)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Cerrar
            </Button>
            <Button onClick={handleDownloadDetalle}>
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
